import React, { useContext, useState } from 'react';
import { AppContext } from 'Context';
import {
  Button, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { Plus as PlusIcon } from 'lucide-react';
import { AnimatedCategory, AnimatedPlaylist, AnimatedSubcategory } from 'Components/reactbits/AnimatedIcons';

// Single "Create" control combining Category / Playlist / Subcategory. Each item
// opens the corresponding globally-mounted modal via AppContext setters. Icons are the
// same genuinely-animated lucide icons used in the preview sidebar (they animate their
// internal geometry on hover, matching lucide-animated.com).
const CreateMenu = ({ iconOnly = false }) => {
  const {
    setShowAddCategoryModal,
    setCategoryModalEditData,
    setShowAddPlaylistModal,
    setShowAddSubCategoryModal,
  } = useContext(AppContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const close = () => setAnchorEl(null);

  const items = [
    {
      label: 'Category',
      Icon: AnimatedCategory,
      onClick: () => { setCategoryModalEditData?.(null); setShowAddCategoryModal?.(true); },
    },
    {
      label: 'Playlist',
      Icon: AnimatedPlaylist,
      onClick: () => setShowAddPlaylistModal?.(true),
    },
    {
      label: 'Subcategory',
      Icon: AnimatedSubcategory,
      onClick: () => setShowAddSubCategoryModal?.(true),
    },
  ];

  return (
    <>
      <Button
        variant="outlined"
        startIcon={iconOnly ? undefined : <PlusIcon size={18} strokeWidth={2.4} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={iconOnly ? 'Create' : undefined}
        title={iconOnly ? 'Create' : undefined}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: iconOnly ? '50%' : 2.5,
          borderColor: '#e4e7ec',
          color: '#15171c',
          px: iconOnly ? 0 : 2,
          minWidth: iconOnly ? 40 : undefined,
          width: iconOnly ? 40 : undefined,
          height: iconOnly ? 40 : undefined,
          '&:hover': { borderColor: '#15171c', bgcolor: 'transparent' },
          '& .lucide': { transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)' },
          '&:hover .lucide': { transform: 'rotate(90deg)' },
        }}
      >
        {iconOnly ? <PlusIcon size={19} strokeWidth={2.4} /> : 'Create'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 210 } }}
      >
        {items.map(({ label, Icon, onClick }, idx) => (
          <MenuItem
            key={label}
            onClick={() => { close(); onClick(); }}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
            sx={{ py: 1.1, gap: 0.5 }}
          >
            <ListItemIcon sx={{ color: '#15171c', minWidth: 34 }}>
              <Icon size={18} strokeWidth={2.2} animate={hoveredIdx === idx} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}>
              {`New ${label}`}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CreateMenu;
